import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Heading } from '@shieldpay/bumblebee/heading';
import TabsMarkdown from '@shieldpay/bumblebee/markdown/tabs.md';
import { Text } from '@shieldpay/bumblebee/text';
import { TextList } from '@shieldpay/bumblebee/text-list';

import { Tabs } from '@shieldpay/utilities-ui/tabs';

import { ContentsTable } from '~/components/contents-table/contents-table';
import { Design } from '~/components/design/design';
import { MdxFormatter } from '~/components/mdx-formatter/mdx-formatter';
import { ComponentPreview } from '~/components/preview/component-preview';
import { createMeta } from '~/utilities/create-meta';

export const meta = createMeta({
  id: 'hive.components.tabs.title',
});

export const TabsComponentPage = () => {
  const [selectedTab, setSelectedTab] = useState<string>('tab-1');

  const handleClick = (id: string) => {
    setSelectedTab(id);
  };

  return (
    <>
      <Heading>
        <FormattedMessage id="hive.components.tabs.title" />
      </Heading>

      <ContentsTable />

      <ComponentPreview spacing="basePos5">
        <Heading level="3" variant="title">
          Tabs as navlinks
        </Heading>

        <Tabs>
          <Tabs.Item to="/components/tabs/1">Tab one</Tabs.Item>
          <Tabs.Item to="/components/tabs/2">
            The long title of tab two
          </Tabs.Item>
          <Tabs.Item to="/components/tabs/3">The third tab</Tabs.Item>
          <Tabs.Item to="/" disabled>
            A disabled tab
          </Tabs.Item>
        </Tabs>

        <Heading level="3" variant="title">
          Tabs as buttons
        </Heading>

        <Tabs>
          <Tabs.Item id="tab-1" onClick={handleClick} selectedTab={selectedTab}>
            Tab one
          </Tabs.Item>
          <Tabs.Item id="tab-2" onClick={handleClick} selectedTab={selectedTab}>
            The long title of tab two
          </Tabs.Item>
          <Tabs.Item id="tab-3" onClick={handleClick} selectedTab={selectedTab}>
            The third tab
          </Tabs.Item>
          <Tabs.Item
            disabled
            id="tab-4"
            onClick={handleClick}
            selectedTab={selectedTab}
          >
            A disabled tab
          </Tabs.Item>
        </Tabs>
      </ComponentPreview>

      <Design>
        <Design.Section heading="Usage">
          <Text paragraph>
            Tabs should be used in conjunction with cards to give the user
            multiple views.
          </Text>
          <Text paragraph>
            Tabs should ideally be applied to information within a card that can
            be split evenly and easily digestible, for example multiple tables
            for one entity.
          </Text>
          <Text paragraph>
            Write short tab labels and use plain language. Tab labels should
            usually be 1–2 words. Short labels are more scannable; if you need
            longer labels, it’s a sign that the choices are too complicated for
            a tab control.
          </Text>
        </Design.Section>
        <Design.Section heading="Anatomy">
          <Text paragraph>
            Tabs consist of two or more text links with a visual indicator of
            which tab is currently selected.
          </Text>
          <Text paragraph>
            To maintain an uncluttered UI no more than 6 tabs should be used
            together.
          </Text>
        </Design.Section>
        <Design.Section heading="Accessibility">
          <Text paragraph>
            Use the appropriate ARIA attributes for screen readers where
            applicable.
          </Text>
        </Design.Section>

        <Design.Section heading="Behaviour">
          <Text paragraph>
            Tabs can be clicked, tapped and selected by tabbing on a keyboard.
            Typically the first tab will be selected by default.
          </Text>
          <Text paragraph>
            If the user navigates back using the breadcrumb, the previously
            selected tab should be selected. i.e. if I navigate forward from Tab
            2, then it should be selected if I reverse my navigation.
          </Text>
          <Text paragraph>
            If the users refreshes the page the current selected tab should
            remain selected. (Eg through query params).
          </Text>
        </Design.Section>

        <Design.Section heading="States">
          <Text>See the different states of the nav buttons below</Text>
          <TextList>
            <TextList.Item>
              Idle Text - neutral-500 Fill -neutral-000 Stroke - none Highlight
              - neutral-100
            </TextList.Item>

            <TextList.Item>
              Hover Text - neutral-500 Fill - neutral-100 Stroke - none
              Highlight - neutral-100
            </TextList.Item>
            <TextList.Item>
              Focus Text - neutral-500 Fill - neutral-000 Stroke - primary-400
              Highlight - neutral-100
            </TextList.Item>
            <TextList.Item>
              Selected Text - neutral-500 Fill - neutral-000 Stroke - none
              Highlight - primary-500
            </TextList.Item>
            <TextList.Item>
              Disabled Text - neutral-250 Fill - neutral-000 Stroke - none
              Highlight - neutral-100
            </TextList.Item>
          </TextList>
        </Design.Section>

        <Design.Section heading="Spacing">
          <TextList>
            <TextList.Item>
              Spacing between tabs: base-positive-02
            </TextList.Item>

            <TextList.Item>
              Spacing between tab text and highlight: base-negative-06
            </TextList.Item>
            <TextList.Item>
              The length of the content will determine the size of the tab
            </TextList.Item>
          </TextList>
        </Design.Section>
      </Design>

      <MdxFormatter md={TabsMarkdown} />
    </>
  );
};

export default TabsComponentPage;
